import { Injectable } from '@angular/core'
import { ActionsSubject, select, Store } from '@ngrx/store'
import * as UnitsReducer from '../reducers'
import * as ControlledInputsActions from '../actions/controlled-inputs.actions'
import * as PositionsActions from '../actions/positions.actions'
import * as UnitsAllDataActions from '../actions/unit-all-data.actions'
import * as UnitGroupsActions from '../actions/unit-groups.actions'
import * as UnitsActions from '../actions/units.actions'
import * as UnitsApiActions from '../actions/units-api.actions'
import * as UnitsSharedActions from '../actions/units-shared.actions'
import { ControlledInputUpdate } from '../../../units/models/controlled-input-update'
import { Position } from '../../../units/models/position'
import { UnitAllData } from '../../../units/models/unit-all-data'
import { UnitStats } from '../../../units/models/unit-stats'
import { ofType } from '@ngrx/effects'
import { ControlledInputs } from '../../../units/models/controlled-inputs'
import { UnitsService } from '../../../units/services/units.service'

@Injectable({
  providedIn: 'root',
})
export class UnitsFacade {
  // Units
  public getUnits$ = this._store.select(UnitsReducer.getUnits)
  public getUnitsByIds$ = unitIds => this._store.select(UnitsReducer.getUnitsByIds, unitIds)
  public areUnitsLoading$ = this._store.select(UnitsReducer.areUnitsLoading)
  public updateUnitNameFailure$ = this._actionsSubj.pipe(ofType(UnitsApiActions.updateUnitNameFailure.type))
  public getUnitsSuccess$ = this._actionsSubj.pipe(ofType(UnitsApiActions.getUnitsSuccess.type))

  // Unit groups
  public getUnitGroups$ = this._store.select(UnitsReducer.getUnitGroups)

  // Positions
  public getPositions$ = this._store.select(UnitsReducer.getPositions)
  public getPositionsLastUpdate$ = this._store.select(UnitsReducer.getPositionsLastUpdate)
  public getPositionCache$ = this._store.pipe(select(UnitsReducer.getPositionCache))
  public arePositionsLoading$ = this._store.pipe(select(UnitsReducer.arePositionsLoading))

  // Units all data
  public getUnitAllData$ = this._store.select(UnitsReducer.getUnitAllData)
  public getUnitDataById$ = unitId => this._store.select(UnitsReducer.getUnitDataById$, unitId)
  public getSelectedUnitData$ = this._store.select(UnitsReducer.getSelectedUnitData)
  public getUnitData$ = this._store.select(UnitsReducer.getUnitData)
  public getZoomUnit$ = this._store.select(UnitsReducer.getZoomUnit)
  public getUnitStats$ = this._store.select(UnitsReducer.getUnitStats)

  // Controlled inputs
  public getAllControlledInputs$ = this._store.select(UnitsReducer.getAllControlledInputs)

  public getGroupedUnits$ = this._store.select(UnitsReducer.getGroupedUnits)
  public getGroupedUnitsDataSource$ = this._store.select(UnitsReducer.getGroupedUnitsDataSource)
  public areUnitsDataLoading$ = this._store.select(UnitsReducer.areUnitsDataLoading)

  constructor(private _store: Store<any>, private _actionsSubj: ActionsSubject, private _unitsService: UnitsService) {}

  // Controlled inputs
  public getControlledInputs(unitIds) {
    this._store.dispatch(ControlledInputsActions.getControlledInputs({ unitIds }))
  }
  public updateControlledInput(data: ControlledInputUpdate) {
    this._store.dispatch(ControlledInputsActions.updateControlledInput({ data }))
  }
  public cancelControlledInputUpdate(unitId: number, inputName: string) {
    this._store.dispatch(ControlledInputsActions.cancelControlledInputUpdate({ data: { unitId, inputName } }))
  }
  public wsControlledInputUpdateSuccess(data: ControlledInputs[]) {
    this._store.dispatch(ControlledInputsActions.wsControlledInputUpdateSuccess({ data }))
  }

  // Positions
  public getPositions(unitIds: number[]) {
    this._store.dispatch(PositionsActions.getPositions({ unitIds }))
  }
  public updatePositions(positions: Position[]) {
    this._store.dispatch(PositionsActions.updatePositions({ positions }))
  }

  // Units all data
  public mergePositionsWithUnits(unitAllData: UnitAllData[]) {
    this._store.dispatch(UnitsAllDataActions.mergePositionsWithUnits({ unitAllData }))
  }
  public updateUnits(unitAllData: UnitAllData[]) {
    this._store.dispatch(UnitsAllDataActions.updateUnits({ unitAllData }))
  }
  public selectUnit(unitData: UnitAllData) {
    this._store.dispatch(UnitsAllDataActions.selectUnit({ unitData }))
  }
  public selectUnits(unitData: UnitAllData[]) {
    this._store.dispatch(UnitsAllDataActions.selectUnits({ unitData }))
  }
  public zoomUnit(unitData: UnitAllData) {
    this._store.dispatch(UnitsAllDataActions.zoomUnit({ unitData }))
  }
  public prepareUnitsStats(unitAllData: UnitAllData[]) {
    this._store.dispatch(UnitsAllDataActions.prepareUnitsStats({ unitAllData }))
  }
  public setUnitsStats(unitStats: UnitStats) {
    this._store.dispatch(UnitsAllDataActions.setUnitsStats({ unitStats }))
  }

  // Groups
  public getUnitGroups() {
    this._store.dispatch(UnitGroupsActions.getUnitGroups())
  }

  // Units
  public getUnits() {
    this._store.dispatch(UnitsActions.getUnits())
  }
  public updateUnitName(unitId: number, name: string) {
    this._store.dispatch(UnitsActions.updateUnitName({ unitId, name }))
  }
  public getUnitById(unitId: number) {
    return this._unitsService.getUnitById(unitId)
  }

  // Units shared
  public getData() {
    this._store.dispatch(UnitsSharedActions.getData())
  }
  public clearData() {
    this._store.dispatch(UnitsSharedActions.clearData())
  }
}
